<div class="driver-page">
  @if (overview(); as page) {
    <section class="driver-hero">
      <div class="driver-hero-copy">
        <p class="driver-eyebrow">DriveMe Academy</p>
        <h1>Fahrerübersicht</h1>
        <p>Überblick über Fahrer, Zugänge, Fristen und Schulungsstand.</p>

        <div class="driver-hero-meta">
          <span>{{ page.total }} Treffer</span>
          <span>{{ page.stats.activeAccessDrivers }} aktive Zugänge</span>
          <span>{{ page.stats.criticalDrivers }} mit Handlungsbedarf</span>
        </div>
      </div>

      <div class="driver-hero-actions">
        <button type="button" class="ghost-button" (click)="openTrashDialog()">Papierkorb</button>
        <button type="button" class="primary-button" (click)="openCreateDriverDialog()">Fahrer anlegen</button>
      </div>
    </section>

    <section class="kpi-grid">
      <article class="driver-metric-card accent">
        <span>Fahrer gesamt</span>
        <strong>{{ page.stats.totalDrivers }}</strong>
      </article>

      <article class="driver-metric-card">
        <span>Aktive Zugänge</span>
        <strong class="metric-green">{{ page.stats.activeAccessDrivers }}</strong>
      </article>

      <article class="driver-metric-card">
        <span>Deaktivierte Zugänge</span>
        <strong class="metric-muted">{{ page.stats.inactiveAccessDrivers }}</strong>
      </article>

      <a class="driver-metric-card dashboard-action-card danger" routerLink="/drivers" [queryParams]="{ status: 'CRITICAL' }">
        <span>Handlungsbedarf</span>
        <strong class="metric-red">{{ page.stats.criticalDrivers }}</strong>
      </a>
    </section>

    <section class="driver-filter-shell">
      <div class="driver-filter-grid">
        <label>
          <span>Suche</span>
          <input
            type="search"
            placeholder="Name, E-Mail, Telefon oder Fahrernummer"
            [value]="search()"
            (input)="updateSearch($event)"
          >
        </label>

        <label>
          <span>Unternehmer</span>
          <select [value]="companyFilter()" (change)="updateCompanyFilter($event)">
            <option value="">Alle Unternehmer</option>
            @for (company of companies(); track company.id) {
              <option [value]="company.id">{{ company.name }}</option>
            }
          </select>
        </label>

        <label>
          <span>Status</span>
          <select [value]="statusFilter()" (change)="updateStatusFilter($event)">
            <option value="">Alle</option>
            <option value="ACCESS_ACTIVE">Zugang aktiv</option>
            <option value="ACCESS_INACTIVE">Zugang deaktiviert</option>
            <option value="CRITICAL">Handlungsbedarf</option>
            <option value="READY">Einsatzbereit</option>
            <option value="ACTIVE">Aktiv</option>
            <option value="TRAINING_IN_PROGRESS">In Schulung</option>
            <option value="RETRAINING_REQUIRED">Nachschulung erforderlich</option>
            <option value="OVERDUE">Überfällig</option>
            <option value="BLOCKED">Gesperrt</option>
            <option value="INACTIVE">Inaktiv</option>
          </select>
        </label>

        <div class="driver-filter-actions">
          <button type="button" class="primary-button" (click)="applyFilters()">Filtern</button>
          <button type="button" class="ghost-button" (click)="resetFilters()">Zurücksetzen</button>
        </div>
      </div>

      <div class="driver-results-row">
        <span class="driver-result-pill">{{ page.total }} Treffer</span>
        <p class="driver-page-note">Verwaltungssicht für angeschlossene Unternehmer und Fahrer.</p>
      </div>
    </section>

    @if (notice()) {
      <p class="info-message">{{ notice() }}</p>
    }

    <section class="driver-card-list">
      @for (driver of page.items; track driver.id) {
        <article class="driver-card-shell" [class.critical]="driver.critical">
          <div class="driver-card-content">
            <div class="driver-card-main">
              <div class="driver-identity-block">
                <div class="driver-avatar" aria-hidden="true">{{ driverInitials(driver) }}</div>

                <div class="driver-identity-copy">
                  <h3 class="driver-name">
                    <a class="name-link" [routerLink]="['/drivers', driver.id]">{{ driver.displayName }}</a>
                  </h3>

                  <div class="driver-chip-row">
                    <span [class.driver-chip-active]="driver.accessActive" [class.driver-chip-muted]="!driver.accessActive">
                      {{ driver.accessActive ? 'Zugang aktiv' : 'Zugang deaktiviert' }}
                    </span>

                    @if (driver.critical) {
                      <span class="driver-chip-critical">Handlungsbedarf</span>
                    }
                  </div>

                  <div class="driver-contact">
                    <div>
                      <span class="driver-contact-label">E-Mail</span>
                      <span>{{ driver.email }}</span>
                    </div>
                    <div>
                      <span class="driver-contact-label">Telefon</span>
                      <span>{{ valueOrDash(driver.phone) }}</span>
                    </div>
                    <div>
                      <span class="driver-contact-label">Unternehmer</span>
                      <span>{{ driver.organizationName }} · {{ driver.companyName }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="driver-facts">
                <div class="driver-fact-grid">
                  <div class="driver-fact-group">
                    <h4>Fahrerdaten</h4>
                    <div>
                      <span>Portal-ID: <strong>{{ portalId(driver) }}</strong></span>
                      <span>Fahrernummer: <strong>{{ valueOrDash(driver.personnelNumber) }}</strong></span>
                      <span>P-Schein gültig bis: <strong [class.warning-text]="!driver.pScheinValidUntil">{{ dateOrMissing(driver.pScheinValidUntil) }}</strong></span>
                      <span>Status aktiv bis: <strong [class.warning-text]="!driver.statusValidUntil">{{ dateOrMissing(driver.statusValidUntil) }}</strong></span>
                    </div>
                  </div>

                  <div class="driver-fact-group">
                    <h4>Status</h4>
                    <div>
                      <strong class="driver-status-value">{{ statusLabel(driver.status) }}</strong>
                      <span>{{ driver.accessActive ? 'Login möglich' : 'Login deaktiviert' }}</span>
                    </div>
                  </div>

                  <div class="driver-fact-group">
                    <div class="driver-progress-head">
                      <h4>Fortschritt</h4>
                      <strong>{{ driver.progressPercent }} %</strong>
                    </div>

                    <div class="driver-progress">
                      <div class="driver-progress-track" aria-hidden="true">
                        <div class="driver-progress-fill" [style.width.%]="driver.progressPercent"></div>
                      </div>
                      <span class="driver-progress-meta">{{ driver.completedLessons }} abgeschlossen / {{ driver.openLessons }} offen</span>
                    </div>
                  </div>
                </div>

                @if (driver.currentCourses.length) {
                  <div class="driver-course-list">
                    @for (course of driver.currentCourses; track course.title) {
                      <span [title]="enrollmentStatusLabel(course.status)">
                        {{ course.title }} · {{ course.progressPercent }} %
                      </span>
                    }
                  </div>
                } @else {
                  <p class="driver-empty-copy">Noch keine Kurszuweisung vorhanden.</p>
                }
              </div>
            </div>

            <aside class="driver-card-actions">
              <a class="primary-button as-link" [routerLink]="['/drivers', driver.id]">Akte öffnen</a>
              <a class="ghost-button as-link" [routerLink]="['/drivers', driver.id, 'edit']">Bearbeiten</a>
            </aside>
          </div>
        </article>
      } @empty {
        <div class="driver-empty">
          Es wurden keine Fahrer gefunden.
          <div class="empty-actions">
            <button type="button" class="primary-button" (click)="openCreateDriverDialog()">Ersten Fahrer anlegen</button>
          </div>
        </div>
      }
    </section>

    @if (createDriverDialogOpen()) {
      <div class="modal-backdrop" role="presentation" (click)="closeCreateDriverDialog()">
        <section class="modal-panel wide-modal" role="dialog" aria-modal="true" aria-labelledby="create-driver-title" (click)="$event.stopPropagation()">
          <div class="panel-heading">
            <div>
              <h2 id="create-driver-title">Neuen Fahrer anlegen</h2>
              <p>Stammdaten, Betrieb und Dokumente erfassen.</p>
            </div>
            <button type="button" class="ghost-button" (click)="closeCreateDriverDialog()">Schließen</button>
          </div>

          <div class="form-with-hint">
            <form class="dialog-form" (submit)="createDriver($event)">
              <label>
                <span>Vorname</span>
                <input type="text" required [value]="driverForm().firstName" (input)="updateDriverForm('firstName', $event)">
              </label>

              <label>
                <span>Nachname</span>
                <input type="text" required [value]="driverForm().lastName" (input)="updateDriverForm('lastName', $event)">
              </label>

              <label>
                <span>E-Mail</span>
                <input type="email" required [value]="driverForm().email" (input)="updateDriverForm('email', $event)">
              </label>

              <label>
                <span>Telefon</span>
                <input type="tel" [value]="driverForm().phone" (input)="updateDriverForm('phone', $event)">
              </label>

              <label>
                <span>Fahrernummer</span>
                <input type="text" [value]="driverForm().personnelNumber" (input)="updateDriverForm('personnelNumber', $event)">
              </label>

              <label>
                <span>Betrieb</span>
                <select required [value]="driverForm().companyId ?? ''" (change)="updateDriverCompany($event)">
                  <option value="">Bitte auswählen</option>
                  @for (company of companies(); track company.id) {
                    <option [value]="company.id">{{ company.name }}</option>
                  }
                </select>
              </label>

              <label class="full-width">
                <span>Fahrergruppe</span>
                <select multiple size="2" (change)="updateDriverGroups($event)">
                  @for (group of availableDriverGroups(); track group.id) {
                    <option [value]="group.id" [selected]="driverForm().groupIds.includes(group.id)">{{ group.name }}</option>
                  }
                </select>
                <small>Wähle eine oder mehrere Fahrergruppen aus, zum Beispiel Neue Fahrer, Nachschulung oder Krankenfahrt.</small>
              </label>

              <label>
                <span>Bevorzugte Sprache</span>
                <select [value]="driverForm().preferredLanguage" (change)="updateDriverForm('preferredLanguage', $event)">
                  <option value="de">Deutsch</option>
                  <option value="en">Englisch</option>
                  <option value="tr">Türkisch</option>
                  <option value="ar">Arabisch</option>
                  <option value="uk">Ukrainisch</option>
                  <option value="ru">Russisch</option>
                </select>
              </label>

              <label>
                <span>P-Schein gültig bis</span>
                <input type="date" [value]="driverForm().pScheinValidUntil" (input)="updateDriverForm('pScheinValidUntil', $event)">
              </label>

              @if (driverCreateError()) {
                <p class="field-error full-width">{{ driverCreateError() }}</p>
              }

              <div class="dialog-actions full-width">
                <button type="submit" class="primary-button" [disabled]="driverSaving()">
                  {{ driverSaving() ? 'Wird angelegt...' : 'Fahrer anlegen' }}
                </button>
                <button type="button" class="ghost-button" (click)="closeCreateDriverDialog()">Abbrechen</button>
              </div>
            </form>

            <aside class="hint-panel">
              <h3>Hinweis</h3>
              <p>Beim Anlegen wird noch kein Passwort vergeben. Sende danach in der Fahrerakte den Passwort-Link an den Fahrer.</p>
            </aside>
          </div>
        </section>
      </div>
    }

    @if (trashDialogOpen()) {
      <div class="modal-backdrop" role="presentation" (click)="closeTrashDialog()">
        <section class="modal-panel wide-modal" role="dialog" aria-modal="true" aria-labelledby="driver-trash-title" (click)="$event.stopPropagation()">
          <div class="panel-heading">
            <div>
              <h2 id="driver-trash-title">Fahrer-Papierkorb</h2>
              <p>Gelöschte Fahrer können wiederhergestellt werden.</p>
            </div>
            <button type="button" class="ghost-button" (click)="closeTrashDialog()">Schließen</button>
          </div>

          @if (deletedDrivers().length) {
            <table class="table">
              <thead>
                <tr>
                  <th>Fahrer</th>
                  <th>Unternehmer</th>
                  <th>Gelöscht am</th>
                  <th>Gelöscht von</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                @for (driver of deletedDrivers(); track driver.id) {
                  <tr>
                    <td>
                      <strong>{{ driver.displayName }}</strong><br>
                      <span>{{ driver.email }}</span>
                    </td>
                    <td>{{ driver.companyName }}</td>
                    <td>{{ formatDate(driver.deletedAt) }}</td>
                    <td>{{ valueOrDash(driver.deletedByName) }}</td>
                    <td><button type="button" class="primary-button" (click)="restoreDriver(driver)">Wiederherstellen</button></td>
                  </tr>
                }
              </tbody>
            </table>
          } @else {
            <div class="empty">Der Papierkorb ist leer.</div>
          }
        </section>
      </div>
    }
  } @else {
    <div class="driver-empty">Fahrer werden geladen.</div>
  }
</div>
